import React, { useState } from "react";
import KuikaSlider from "../../../../ui-kit/kuika-slider/kuika-slider";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

type SliderSize = "sm" | "md" | "lg";

interface IStatefulProps {
  initial?: number;
  size?: SliderSize;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  unit?: string;
  showInput?: boolean;
  disabled?: boolean;
}

const Stateful: React.FC<IStatefulProps> = ({ initial = 50, ...rest }) => {
  const [value, setValue] = useState<number>(initial);
  return <KuikaSlider value={value} onChange={(v: number) => setValue(v)} {...rest} />;
};

export const SliderStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Slider"
      componentName="KuikaSlider"
      sourcePath="ui-kit/kuika-slider"
      description="Numeric range control. Three heights, optional inline input, configurable precision and unit suffix."
    >
      <VariantGrid
        label="size"
        caption="default range 0–100, value 50, with inline input"
        cols={["sm", "md", "lg"]}
        cellMinWidth={240}
        render={(size: string): React.ReactNode => (
          <div style={{ width: "100%" }}>
            <Stateful size={size as SliderSize} initial={50} />
          </div>
        )}
      />
      <VariantGrid
        label="state · size"
        caption="disabled dims to --disabled-opacity and hands interaction entirely to Radix's own disabled state; every size still pads the thumb's tap target up to the 32px --hit-min baseline, even the 12px sm circle"
        rows={["enabled", "disabled"]}
        cols={["sm", "md", "lg"]}
        cellMinWidth={220}
        render={(state: string, size: string): React.ReactNode => (
          <div style={{ width: "100%" }}>
            <KuikaSlider value={50} size={size as SliderSize} disabled={state === "disabled"} onChange={noop} />
          </div>
        )}
      />
      <SpecRow label="Without inline input" caption="showInput=false — pure track" align="stretch">
        <div style={{ width: 360 }}>
          <Stateful initial={30} showInput={false} />
        </div>
      </SpecRow>
      <SpecRow label="With unit" caption="percentage / pixel suffix" align="stretch">
        <div style={{ width: 320 }}>
          <Stateful initial={42} unit="%" />
        </div>
        <div style={{ width: 320 }}>
          <Stateful initial={24} min={0} max={64} unit="px" />
        </div>
      </SpecRow>
      <SpecRow label="Precision · step" caption="float steps with rounding" align="stretch">
        <div style={{ width: 320 }}>
          <Stateful initial={1.25} min={0} max={2} step={0.05} precision={2} />
        </div>
        <div style={{ width: 320 }}>
          <Stateful initial={0.5} min={0} max={1} step={0.1} precision={1} />
        </div>
      </SpecRow>
      <SpecRow label="Custom range" caption="negative / large ranges" align="stretch">
        <div style={{ width: 320 }}>
          <Stateful initial={-25} min={-100} max={100} unit="%" />
        </div>
        <div style={{ width: 320 }}>
          <Stateful initial={1500} min={0} max={5000} step={100} />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
